import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaShieldAlt, FaBoxOpen } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { isAdminUser } from "../utils/roleUtils";
import { decorateUiItems, findUiItemById } from "../utils/uiConfig";
import { getUiIcon } from "../utils/uiIcons";
import "./SmartToolsHome.css";

function SmartToolsHome() {
  const navigate = useNavigate();
  const { user, uiConfig } = useContext(AuthContext);
  const isAdmin = isAdminUser(user);

  const { hasMapUpdate, hasAmazon, syncTools, asyncTools } = useMemo(() => {
    const group = findUiItemById(uiConfig?.sidebarItems || [], "smart-tools");
    const decorated = decorateUiItems(group?.children || [], { isAdmin })
      .filter((item) => item.id !== "smart-tools-home")
      .map((item) => ({ ...item, Icon: getUiIcon(item.icon) }));
    const inMapUpdate = (item) => item.group === "map-update";
    const inAmazon = (item) => item.group === "amazon-manager";
    const grouped = (item) => inMapUpdate(item) || inAmazon(item);
    return {
      hasMapUpdate: decorated.some(inMapUpdate),
      hasAmazon: decorated.some(inAmazon),
      syncTools: decorated.filter((item) => !grouped(item) && item.jobType !== "async"),
      asyncTools: decorated.filter((item) => !grouped(item) && item.jobType === "async"),
    };
  }, [isAdmin, uiConfig]);

  const renderCard = (tool) => {
    const Icon = tool.Icon;
    return (
      <div
        key={tool.id}
        className="tool-card-premium"
        onClick={() => navigate(tool.route)}
        style={{ "--accent-color": tool.accentColor || "#3b82f6" }}
      >
        <div className="tool-icon-circle">
          <Icon />
        </div>
        <div className="tool-info">
          <h3 className="tool-name">{tool.label}</h3>
          <p className="tool-desc">{tool.description}</p>
        </div>
        <div className="tool-arrow">-></div>
      </div>
    );
  };

  return (
    <div className="tools-wrapper">
      <div className="tools-header-section">
        <h2 className="tools-title-main">Smart Tools</h2>
      </div>

      {(hasMapUpdate || hasAmazon) && (
        <section className="tools-section">
          <div className="tools-section-header">
            <h3 className="tools-section-title">Widgets</h3>
          </div>
          <div className="tools-grid-container">
            {hasMapUpdate && (
              <div
                className="tool-card-premium"
                onClick={() => navigate("/tools/map-update")}
                style={{ "--accent-color": "#10b981" }}
              >
                <div className="tool-icon-circle">
                  <FaShieldAlt />
                </div>
                <div className="tool-info">
                  <h3 className="tool-name">Map Update</h3>
                  <p className="tool-desc">
                    Find products by SKU and manage MAP policies
                  </p>
                </div>
                <div className="tool-arrow">-></div>
              </div>
            )}
            {hasAmazon && (
              <div
                className="tool-card-premium"
                onClick={() => navigate("/tools/amazon-manager")}
                style={{ "--accent-color": "#ff9900" }}
              >
                <div className="tool-icon-circle">
                  <FaBoxOpen />
                </div>
                <div className="tool-info">
                  <h3 className="tool-name">Amazon Manager</h3>
                  <p className="tool-desc">
                    Manage Amazon listings, pricing, Buy Box, and inventory
                  </p>
                </div>
                <div className="tool-arrow">-></div>
              </div>
            )}
          </div>
        </section>
      )}

      {syncTools.length > 0 && (
        <section className="tools-section">
          <div className="tools-section-header">
            <h3 className="tools-section-title">Synchronous Jobs</h3>
          </div>
          <div className="tools-grid-container">{syncTools.map(renderCard)}</div>
        </section>
      )}

      {asyncTools.length > 0 && (
        <section className="tools-section">
          <div className="tools-section-header">
            <h3 className="tools-section-title">Asynchronous Jobs</h3>
          </div>
          <div className="tools-grid-container">{asyncTools.map(renderCard)}</div>
        </section>
      )}
    </div>
  );
}

export default SmartToolsHome;
